import { useMemo, useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar, Doughnut } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
);

// ─── SAMPLE DATA ─────────────────────────────────────────────────────────────
// All numbers here are illustrative. Replace with real GraphQL query results.
//
// Assessment attempt statuses:
//   pass        → user completed the final assessment and passed
//   fail        → user completed the final assessment and failed
//   incomplete  → user started the assessment but did not finish (left in between)
//
// When wiring real data:
//   enrollments  → COUNT of courses_user_course_enrolment where partner_id = 23
//   pass         → COUNT of courses_user_course_quiz_attempt where result = 'pass'
//                  (or equivalent field in your schema)
//   fail         → COUNT where result = 'fail'
//   incomplete   → COUNT where attempt_end IS NULL (started but never finished)
//   stateWise    → above counts grouped by user.address.state
//   districtWise → above counts grouped by user.address.district

const COURSES = [
  "Retail Sales & CS",
  "Data Entry Ops",
  "Hospitality",
  "Beauty & Wellness",
  "Plumbing",
  "Electrician Basics",
  "Driving Skills",
];

const STATES = [
  "Uttar Pradesh",
  "Rajasthan",
  "Bihar",
  "Maharashtra",
  "Madhya Pradesh",
  "Gujarat",
  "Odisha",
  "West Bengal",
];

const DISTRICTS = [
  "Lucknow",
  "Jaipur",
  "Patna",
  "Pune",
  "Bhopal",
  "Surat",
  "Bhubaneswar",
  "Kolkata",
];

// Accumulated data across ALL courses
const ACCUMULATED = {
  // Source: COUNT courses_user_course_enrolment where partner_id = 23
  totalEnrollments: 8342,

  // Source: COUNT courses_user_course_quiz_attempt where partner_id = 23
  // (only final assessment attempts, not intermediate quizzes)
  totalAttempts: 6890,

  // Source: COUNT where result = 'pass' (or equivalent)
  totalPass: 4210,

  // Source: COUNT where result = 'fail'
  totalFail: 1680,

  // Source: COUNT where attempt_end IS NULL (started, never submitted)
  totalIncomplete: 1000,

  // Source: above 5 metrics grouped by user.address.state, top 8 by enrollment
  // Shape: [{ state, enrollments, attempts, pass, fail, incomplete }]
  stateWise: [
    {
      state: "Uttar Pradesh",
      enrollments: 1820,
      attempts: 1490,
      pass: 910,
      fail: 380,
      incomplete: 200,
    },
    {
      state: "Rajasthan",
      enrollments: 1340,
      attempts: 1100,
      pass: 680,
      fail: 270,
      incomplete: 150,
    },
    {
      state: "Bihar",
      enrollments: 1120,
      attempts: 910,
      pass: 550,
      fail: 230,
      incomplete: 130,
    },
    {
      state: "Maharashtra",
      enrollments: 980,
      attempts: 810,
      pass: 490,
      fail: 200,
      incomplete: 120,
    },
    {
      state: "Madhya Pradesh",
      enrollments: 760,
      attempts: 620,
      pass: 380,
      fail: 160,
      incomplete: 80,
    },
    {
      state: "Gujarat",
      enrollments: 640,
      attempts: 530,
      pass: 320,
      fail: 130,
      incomplete: 80,
    },
    {
      state: "Odisha",
      enrollments: 510,
      attempts: 420,
      pass: 260,
      fail: 105,
      incomplete: 55,
    },
    {
      state: "West Bengal",
      enrollments: 480,
      attempts: 400,
      pass: 240,
      fail: 100,
      incomplete: 60,
    },
  ],

  // Source: same metrics grouped by user.address.district, top 8 by enrollment
  // Shape: [{ district, enrollments, attempts, pass, fail, incomplete }]
  districtWise: [
    {
      district: "Lucknow",
      enrollments: 640,
      attempts: 520,
      pass: 315,
      fail: 130,
      incomplete: 75,
    },
    {
      district: "Jaipur",
      enrollments: 520,
      attempts: 430,
      pass: 260,
      fail: 110,
      incomplete: 60,
    },
    {
      district: "Patna",
      enrollments: 490,
      attempts: 400,
      pass: 240,
      fail: 100,
      incomplete: 60,
    },
    {
      district: "Pune",
      enrollments: 430,
      attempts: 355,
      pass: 215,
      fail: 90,
      incomplete: 50,
    },
    {
      district: "Bhopal",
      enrollments: 380,
      attempts: 310,
      pass: 190,
      fail: 80,
      incomplete: 40,
    },
    {
      district: "Surat",
      enrollments: 320,
      attempts: 265,
      pass: 160,
      fail: 68,
      incomplete: 37,
    },
    {
      district: "Bhubaneswar",
      enrollments: 290,
      attempts: 240,
      pass: 145,
      fail: 62,
      incomplete: 33,
    },
    {
      district: "Kolkata",
      enrollments: 270,
      attempts: 222,
      pass: 135,
      fail: 57,
      incomplete: 30,
    },
  ],
};

// Per-course data — same shape as ACCUMULATED but scoped to one course
// Source: same queries filtered by course_id
const COURSE_WISE = COURSES.map((courseName, idx) => {
  const base = [2100, 1840, 1420, 980, 750, 680, 572][idx];
  const att = Math.round(base * 0.82);
  const pass = Math.round(att * 0.61);
  const fail = Math.round(att * 0.24);
  const inc = att - pass - fail;

  return {
    courseName,
    totalEnrollments: base,
    totalAttempts: att,
    totalPass: pass,
    totalFail: fail,
    totalIncomplete: inc,

    // Same state breakdown scoped to this course
    stateWise: STATES.map((state, si) => {
      const e = Math.round(
        (base / 8) * [1.4, 1.1, 0.9, 0.8, 0.6, 0.5, 0.4, 0.4][si],
      );
      const a = Math.round(e * 0.82);
      const p = Math.round(a * 0.61);
      const f = Math.round(a * 0.24);
      return {
        state,
        enrollments: e,
        attempts: a,
        pass: p,
        fail: f,
        incomplete: a - p - f,
      };
    }),

    // Same district breakdown scoped to this course
    districtWise: DISTRICTS.map((district, di) => {
      const e = Math.round(
        (base / 8) * [0.5, 0.4, 0.38, 0.33, 0.29, 0.25, 0.22, 0.21][di],
      );
      const a = Math.round(e * 0.82);
      const p = Math.round(a * 0.61);
      const f = Math.round(a * 0.24);
      return {
        district,
        enrollments: e,
        attempts: a,
        pass: p,
        fail: f,
        incomplete: a - p - f,
      };
    }),
  };
});
// ─── END SAMPLE DATA ─────────────────────────────────────────────────────────

// ─── CONSTANTS ───────────────────────────────────────────────────────────────
const C_ENROLL = "#2a78d6";
const C_ATTEMPTS = "#eda100";
const C_PASS = "#1baf7a";
const C_FAIL = "#e34948";
const C_INCOMPLETE = "#888780";

const TICK = { color: "#898781" };
const GRID = { color: "#e1e0d9" };
const NOGRID = { display: false };
const BORDER = { color: "#e1e0d9" };

const BAR_OPTS_H = {
  indexAxis: "y",
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { display: false }, tooltip: { mode: "index" } },
  scales: {
    x: { stacked: true, ticks: TICK, grid: GRID, border: BORDER },
    y: { stacked: true, ticks: TICK, grid: NOGRID, border: BORDER },
  },
};

// ─── SUB-COMPONENTS ──────────────────────────────────────────────────────────
function SectionLabel({ children }) {
  return (
    <div className="text-xs font-medium tracking-widest uppercase text-gray-400 mb-3">
      {children}
    </div>
  );
}

function ChartCard({ title, sub, height = 300, children }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5">
      <div className="text-sm font-medium text-gray-900 mb-0.5">{title}</div>
      {sub && <div className="text-xs text-gray-400 mb-3">{sub}</div>}
      {children}
    </div>
  );
}

function KpiStrip({ enrollments, attempts, pass, fail, incomplete }) {
  const items = [
    { label: "Enrollments", val: enrollments, color: C_ENROLL },
    { label: "Assessment attempts", val: attempts, color: C_ATTEMPTS },
    { label: "Pass", val: pass, color: C_PASS },
    { label: "Fail", val: fail, color: C_FAIL },
    { label: "Did not complete", val: incomplete, color: C_INCOMPLETE },
    { label: "Certificates issued", val: pass, color: "#c8982a" },
  ];
  return (
    <div className="grid grid-cols-5 gap-3 mb-6">
      {items.map(({ label, val, color }) => (
        <div
          key={label}
          className="bg-gray-50 border border-gray-200 rounded-lg p-3"
          style={{ borderTop: `3px solid ${color}` }}
        >
          <div className="text-xs text-gray-400 mb-1">{label}</div>
          <div className="text-2xl font-bold text-gray-900">
            {val.toLocaleString()}
          </div>
        </div>
      ))}
    </div>
  );
}

function ChartLegend() {
  return (
    <div className="flex flex-wrap gap-3 mb-3 text-xs text-gray-500">
      {[
        { label: "Enrollments", color: C_ENROLL },
        { label: "Attempts", color: C_ATTEMPTS },
        { label: "Pass", color: C_PASS },
        { label: "Fail", color: C_FAIL },
        { label: "Did not complete", color: C_INCOMPLETE },
      ].map(({ label, color }) => (
        <span key={label} className="flex items-center gap-1">
          <span
            className="inline-block w-2.5 h-2.5 rounded-sm"
            style={{ background: color }}
          ></span>
          {label}
        </span>
      ))}
    </div>
  );
}

function makeStackedDataset(rows, labelKey) {
  return {
    labels: rows.map((r) => r[labelKey]),
    datasets: [
      {
        label: "Enrollments",
        data: rows.map((r) => r.enrollments),
        backgroundColor: C_ENROLL,
        borderRadius: 3,
      },
      {
        label: "Attempts",
        data: rows.map((r) => r.attempts),
        backgroundColor: C_ATTEMPTS,
        borderRadius: 3,
      },
      {
        label: "Pass",
        data: rows.map((r) => r.pass),
        backgroundColor: C_PASS,
        borderRadius: 3,
      },
      {
        label: "Fail",
        data: rows.map((r) => r.fail),
        backgroundColor: C_FAIL,
        borderRadius: 3,
      },
      {
        label: "Did not complete",
        data: rows.map((r) => r.incomplete),
        backgroundColor: C_INCOMPLETE,
        borderRadius: 3,
      },
    ],
  };
}

function BreakdownCharts({ stateWise, districtWise, idPrefix }) {
  const stateData = useMemo(
    () => makeStackedDataset(stateWise, "state"),
    [stateWise],
  );
  const districtData = useMemo(
    () => makeStackedDataset(districtWise, "district"),
    [districtWise],
  );
  const stateH = stateWise.length * 44 + 60;
  const districtH = districtWise.length * 44 + 60;

  return (
    <div className="grid grid-cols-2 gap-4">
      <ChartCard
        title="State-wise breakdown"
        sub="Enrollment & assessment results by state"
      >
        <ChartLegend />
        <div style={{ position: "relative", height: stateH }}>
          <Bar
            id={`${idPrefix}-state`}
            data={stateData}
            options={{
              ...BAR_OPTS_H,
              scales: {
                x: { stacked: false, ticks: TICK, grid: GRID, border: BORDER },
                y: {
                  stacked: false,
                  ticks: TICK,
                  grid: NOGRID,
                  border: BORDER,
                },
              },
            }}
          />
        </div>
      </ChartCard>

      <ChartCard
        title="District-wise breakdown"
        sub="Enrollment & assessment results by district"
      >
        <ChartLegend />
        <div style={{ position: "relative", height: districtH }}>
          <Bar
            id={`${idPrefix}-district`}
            data={districtData}
            options={{
              ...BAR_OPTS_H,
              scales: {
                x: { stacked: false, ticks: TICK, grid: GRID, border: BORDER },
                y: {
                  stacked: false,
                  ticks: TICK,
                  grid: NOGRID,
                  border: BORDER,
                },
              },
            }}
          />
        </div>
      </ChartCard>
    </div>
  );
}

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────
export default function Partner23Dashboard() {
  const [selectedCourseIdx, setSelectedCourseIdx] = useState(0);
  const selectedCourse = COURSE_WISE[selectedCourseIdx];

  // Donut for accumulated assessment outcome
  const accDonutData = useMemo(
    () => ({
      labels: ["Pass", "Fail", "Did not complete"],
      datasets: [
        {
          data: [
            ACCUMULATED.totalPass,
            ACCUMULATED.totalFail,
            ACCUMULATED.totalIncomplete,
          ],
          backgroundColor: [C_PASS, C_FAIL, C_INCOMPLETE],
          borderWidth: 0,
          hoverOffset: 4,
        },
      ],
    }),
    [],
  );

  // Donut for selected course assessment outcome
  const courseDonutData = useMemo(
    () => ({
      labels: ["Pass", "Fail", "Did not complete"],
      datasets: [
        {
          data: [
            selectedCourse.totalPass,
            selectedCourse.totalFail,
            selectedCourse.totalIncomplete,
          ],
          backgroundColor: [C_PASS, C_FAIL, C_INCOMPLETE],
          borderWidth: 0,
          hoverOffset: 4,
        },
      ],
    }),
    [selectedCourse],
  );

  const donutOpts = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "68%",
    plugins: { legend: { display: false } },
  };

  return (
    <div className="w-full px-6 py-8 space-y-8">
      {/* ── SECTION 1: ACCUMULATED ─────────────────────────────────────────── */}
      <div>
        <SectionLabel>Accumulated — all courses combined</SectionLabel>

        <KpiStrip
          enrollments={ACCUMULATED.totalEnrollments}
          attempts={ACCUMULATED.totalAttempts}
          pass={ACCUMULATED.totalPass}
          fail={ACCUMULATED.totalFail}
          incomplete={ACCUMULATED.totalIncomplete}
        />

        {/* Assessment outcome donut */}
        <div className="grid grid-cols-3 gap-4 mb-4">
          <ChartCard title="Assessment outcome" sub="All courses combined">
            <div className="flex items-center gap-4">
              <div
                style={{
                  position: "relative",
                  width: 130,
                  height: 130,
                  flexShrink: 0,
                }}
              >
                <Doughnut data={accDonutData} options={donutOpts} />
              </div>
              <div className="space-y-2 text-xs">
                {[
                  { label: "Pass", val: ACCUMULATED.totalPass, color: C_PASS },
                  { label: "Fail", val: ACCUMULATED.totalFail, color: C_FAIL },
                  {
                    label: "Did not complete",
                    val: ACCUMULATED.totalIncomplete,
                    color: C_INCOMPLETE,
                  },
                ].map(({ label, val, color }) => (
                  <div key={label}>
                    <span
                      className="inline-block w-2.5 h-2.5 rounded-sm mr-1.5"
                      style={{ background: color }}
                    ></span>
                    <span className="text-gray-600">{label}</span>
                    <div className="font-bold text-lg text-gray-900 ml-4">
                      {val.toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </ChartCard>

          <div className="col-span-2">
            <ChartCard
              title="Attempt rate by course"
              sub="Enrollments vs total assessment attempts per course"
            >
              <div className="flex gap-3 mb-2 text-xs text-gray-500">
                <span>
                  <span
                    className="inline-block w-2.5 h-2.5 rounded-sm mr-1"
                    style={{ background: C_ENROLL }}
                  ></span>
                  Enrolled
                </span>
                <span>
                  <span
                    className="inline-block w-2.5 h-2.5 rounded-sm mr-1"
                    style={{ background: C_ATTEMPTS }}
                  ></span>
                  Attempts
                </span>
              </div>
              <div style={{ position: "relative", height: 160 }}>
                <Bar
                  data={{
                    labels: COURSE_WISE.map((c) => c.courseName),
                    datasets: [
                      {
                        label: "Enrolled",
                        data: COURSE_WISE.map((c) => c.totalEnrollments),
                        backgroundColor: C_ENROLL,
                        borderRadius: 3,
                      },
                      {
                        label: "Attempts",
                        data: COURSE_WISE.map((c) => c.totalAttempts),
                        backgroundColor: C_ATTEMPTS,
                        borderRadius: 3,
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: { legend: { display: false } },
                    scales: {
                      x: {
                        ticks: { ...TICK, maxRotation: 30, font: { size: 10 } },
                        grid: NOGRID,
                        border: BORDER,
                      },
                      y: { ticks: TICK, grid: GRID, border: BORDER },
                    },
                  }}
                />
              </div>
            </ChartCard>
          </div>
        </div>

        {/* State + District breakdown — accumulated */}
        <BreakdownCharts
          stateWise={ACCUMULATED.stateWise}
          districtWise={ACCUMULATED.districtWise}
          idPrefix="acc"
        />
      </div>

      {/* ── SECTION 2: COURSE-WISE ─────────────────────────────────────────── */}
      <div>
        <SectionLabel>Course-wise breakdown</SectionLabel>

        {/* Course selector tabs */}
        <div className="flex flex-wrap gap-2 mb-5">
          {COURSE_WISE.map((c, i) => (
            <button
              key={c.courseName}
              onClick={() => setSelectedCourseIdx(i)}
              className="px-3 py-1.5 rounded-full text-xs font-medium border transition-colors"
              style={{
                background: i === selectedCourseIdx ? C_ENROLL : "transparent",
                color: i === selectedCourseIdx ? "#fff" : "#52514e",
                borderColor: i === selectedCourseIdx ? C_ENROLL : "#d3d1c7",
              }}
            >
              {c.courseName}
            </button>
          ))}
        </div>

        {/* KPIs for selected course */}
        <KpiStrip
          enrollments={selectedCourse.totalEnrollments}
          attempts={selectedCourse.totalAttempts}
          pass={selectedCourse.totalPass}
          fail={selectedCourse.totalFail}
          incomplete={selectedCourse.totalIncomplete}
        />

        {/* Outcome donut for selected course */}
        <div className="mb-4">
          <ChartCard
            title={`Assessment outcome — ${selectedCourse.courseName}`}
            sub="Pass / fail / did not complete for this course"
          >
            <div className="flex items-center gap-6">
              <div
                style={{
                  position: "relative",
                  width: 130,
                  height: 130,
                  flexShrink: 0,
                }}
              >
                <Doughnut data={courseDonutData} options={donutOpts} />
              </div>
              <div className="flex gap-8 text-xs">
                {[
                  {
                    label: "Pass",
                    val: selectedCourse.totalPass,
                    color: C_PASS,
                    pct: Math.round(
                      (selectedCourse.totalPass /
                        selectedCourse.totalAttempts) *
                        100,
                    ),
                  },
                  {
                    label: "Fail",
                    val: selectedCourse.totalFail,
                    color: C_FAIL,
                    pct: Math.round(
                      (selectedCourse.totalFail /
                        selectedCourse.totalAttempts) *
                        100,
                    ),
                  },
                  {
                    label: "Did not complete",
                    val: selectedCourse.totalIncomplete,
                    color: C_INCOMPLETE,
                    pct: Math.round(
                      (selectedCourse.totalIncomplete /
                        selectedCourse.totalAttempts) *
                        100,
                    ),
                  },
                ].map(({ label, val, color, pct }) => (
                  <div key={label}>
                    <span
                      className="inline-block w-2.5 h-2.5 rounded-sm mr-1.5"
                      style={{ background: color }}
                    ></span>
                    <span className="text-gray-500">{label}</span>
                    <div className="font-bold text-2xl text-gray-900">
                      {val.toLocaleString()}
                    </div>
                    <div className="text-gray-400">{pct}% of attempts</div>
                  </div>
                ))}
              </div>
            </div>
          </ChartCard>
        </div>

        {/* State + District breakdown — course-wise */}
        <BreakdownCharts
          stateWise={selectedCourse.stateWise}
          districtWise={selectedCourse.districtWise}
          idPrefix={`course-${selectedCourseIdx}`}
        />
      </div>
      {/* ── CERTIFICATE SHOWCASE ──────────────────────────────────────────── */}
      <div>
        <SectionLabel>Certificate programme</SectionLabel>
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
          <div className="flex items-start gap-8">
            {/* Demo certificate card */}
            <div
              className="flex-shrink-0 rounded-lg p-6 relative overflow-hidden"
              style={{
                width: 320,
                border: "2.5px solid #c8982a",
                background: "linear-gradient(145deg, #fffdf4 0%, #fef8e3 100%)",
              }}
            >
              {[
                ["top-2 left-2", "border-t-2 border-l-2"],
                ["top-2 right-2", "border-t-2 border-r-2"],
                ["bottom-2 left-2", "border-b-2 border-l-2"],
                ["bottom-2 right-2", "border-b-2 border-r-2"],
              ].map(([pos, border], i) => (
                <div
                  key={i}
                  className={`absolute w-6 h-6 ${pos} ${border} opacity-40`}
                  style={{ borderColor: "#c8982a" }}
                />
              ))}
              <div className="flex justify-between items-start mb-3">
                <div className="text-xs font-bold tracking-widest text-yellow-800 uppercase">
                  SkillsTrainer
                </div>
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center"
                  style={{ background: "#c8982a" }}
                >
                  <span className="text-white text-base">★</span>
                </div>
              </div>
              <div className="text-xs tracking-widest text-yellow-600 uppercase mb-1">
                Certificate of completion
              </div>
              <div className="font-bold text-2xl text-yellow-900 leading-tight mb-1">
                Priya Sharma
              </div>
              <div className="text-xs text-yellow-700 mb-3">
                Retail Sales & Customer Service · NSQF Level 3
              </div>
              <div className="border-t border-yellow-200 pt-3 flex justify-between text-xs text-yellow-600">
                <span>Issued: 12 Jun 2025</span>
                <span>ID: ST-2025-004821</span>
              </div>
            </div>

            {/* Certificate stats */}
            <div className="flex-1">
              <div className="text-sm font-medium text-gray-900 mb-1">
                Certificates at a glance
              </div>
              <div className="text-xs text-gray-400 mb-4">
                Total certificates = total pass, across all courses
              </div>
              <div className="grid grid-cols-3 gap-3">
                {[
                  {
                    val: ACCUMULATED.totalPass.toLocaleString(),
                    lbl: "Total issued",
                  },
                  {
                    val: `${Math.round((ACCUMULATED.totalPass / ACCUMULATED.totalAttempts) * 100)}%`,
                    lbl: "Pass rate",
                  },
                  {
                    val: COURSE_WISE.reduce(
                      (best, c) => (c.totalPass > best.totalPass ? c : best),
                      COURSE_WISE[0],
                    ).courseName,
                    lbl: "Top course by certificates",
                  },
                  {
                    val: ACCUMULATED.stateWise.reduce(
                      (best, s) => (s.pass > best.pass ? s : best),
                      ACCUMULATED.stateWise[0],
                    ).state,
                    lbl: "Top state by certificates",
                  },
                  { val: COURSE_WISE.length, lbl: "Courses covered" },
                  { val: ACCUMULATED.stateWise.length, lbl: "States reached" },
                ].map(({ val, lbl }) => (
                  <div
                    key={lbl}
                    className="bg-white border border-gray-200 rounded-lg p-3"
                    style={{ borderTop: "3px solid #c8982a" }}
                  >
                    <div className="font-bold text-lg text-gray-900 leading-tight">
                      {val}
                    </div>
                    <div className="text-xs text-gray-400 mt-1">{lbl}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
